import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileQuestion, Upload, Loader2, Sparkles, Play, FileText, X, Pencil, Trash2, CheckCircle2, ClipboardList, ShieldAlert, Eye, CalendarClock, Lock } from "lucide-react";

export default function Quiz() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.is_super === true;
  const [quizzes, setQuizzes] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(10);
  const [file, setFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [mTitle, setMTitle] = useState("");
  const [mDuration, setMDuration] = useState(10);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef();

  const [resultsQuiz, setResultsQuiz] = useState(null);
  const [detailAttempt, setDetailAttempt] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [schedQuiz, setSchedQuiz] = useState(null);
  const [sched, setSched] = useState({ open_at: "", close_at: "", manual_state: "auto" });
  const [savingSched, setSavingSched] = useState(false);

  const openSched = (q) => {
    setSchedQuiz(q);
    setSched({ open_at: q.open_at || "", close_at: q.close_at || "", manual_state: q.manual_state || "auto" });
  };

  const saveSched = async (e) => {
    e.preventDefault();
    setSavingSched(true);
    try {
      await api.put(`/quizzes/${schedQuiz.id}/schedule`, sched);
      toast.success("Jadwal kuis disimpan");
      setSchedQuiz(null);
      load();
    } catch (err) { toast.error(errText(err)); } finally { setSavingSched(false); }
  };

  const openDetail = async (attemptId) => {
    setDetailLoading(true);
    setDetailAttempt({ loading: true });
    try {
      const { data } = await api.get(`/quiz-attempts/${attemptId}`);
      setDetailAttempt(data);
    } catch (err) {
      toast.error(errText(err));
      setDetailAttempt(null);
    } finally {
      setDetailLoading(false);
    }
  };
  const [viewQuiz, setViewQuiz] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const openView = async (q) => {
    setViewLoading(true);
    setViewQuiz({ meta: q, questions: null });
    try {
      const { data } = await api.get(`/quizzes/${q.id}/edit`);
      setViewQuiz({ meta: q, questions: data.questions || [] });
    } catch (err) {
      toast.error(errText(err));
      setViewQuiz(null);
    } finally {
      setViewLoading(false);
    }
  };

  const load = () => {
    api.get("/quizzes").then((r) => setQuizzes(r.data));
    api.get("/quiz-attempts").then((r) => setAttempts(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pilih file materi terlebih dahulu");
    setGenerating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("duration_minutes", duration);
      const { data } = await api.post("/quiz/generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Kuis "${data.title}" dibuat (${data.question_count} soal). Tinjau sebelum publikasi.`);
      setOpen(false);
      setTitle("");
      setDuration(10);
      setFile(null);
      navigate(`/kuis/${data.id}/edit`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setGenerating(false);
    }
  };

  const bestScore = (quizId) => {
    const a = attempts.filter((x) => x.quiz_id === quizId);
    return a.length ? Math.max(...a.map((x) => x.score)) : null;
  };

  const createManual = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/quizzes/manual", { title: mTitle, duration_minutes: Number(mDuration) });
      toast.success("Kuis dibuat. Tambahkan soal Anda.");
      setManualOpen(false);
      setMTitle("");
      setMDuration(10);
      navigate(`/kuis/${data.id}/edit`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setCreating(false);
    }
  };

  const removeQuiz = async (id) => {
    if (!window.confirm("Hapus kuis ini beserta soalnya?")) return;
    await api.delete(`/quizzes/${id}`);
    toast.success("Kuis dihapus");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">{isAdmin ? "Manajemen Kuis" : "Kuis Tersedia"}</h1>
          <p className="mt-1 text-slate-500">{isAdmin ? "Unggah materi, AI membuat soal; sunting lalu publikasikan." : "Pilih kuis untuk Anda kerjakan."}</p>
        </div>
        {isAdmin && canEdit && (
          <div className="flex gap-2">
            <button data-testid="open-manual-quiz-button" onClick={() => setManualOpen(true)} className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition">
              <Pencil className="h-4 w-4" /> Tulis Soal Manual
            </button>
            <button data-testid="open-generate-quiz-button" onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
              <Sparkles className="h-4 w-4" /> Buat Kuis dari File
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {quizzes.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <FileQuestion className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-slate-500">Belum ada kuis.</p>
          </div>
        )}
        {quizzes.map((q) => {
          const best = bestScore(q.id);
          const avail = q.availability || { is_available: true, status: "open", label: "Dibuka" };
          const availCls = avail.is_available ? "bg-emerald-50 text-emerald-700" : (avail.status === "not_open" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500");
          return (
            <div key={q.id} data-testid={`quiz-card-${q.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><FileQuestion className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 leading-snug flex-1">{q.title}</p>
                    {isAdmin && (
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${q.published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {q.published ? "PUBLIK" : "DRAFT"}
                      </span>
                    )}
                    <span data-testid={`quiz-availability-${q.id}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${availCls}`}>{avail.label}</span>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400 truncate"><FileText className="h-3 w-3" /> {q.source_filename}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{q.question_count} soal</span>
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">{q.duration_minutes} menit</span>
                </div>
                {!isAdmin && best !== null && <span className="text-xs font-semibold text-emerald-600">Selesai</span>}
              </div>
              {isAdmin ? (
                <div className="mt-4 flex gap-2">
                  <button data-testid={`view-results-${q.id}`} onClick={() => setResultsQuiz(q)} className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition">
                    <ClipboardList className="h-4 w-4" /> Lihat Hasil
                  </button>
                  {canEdit ? (
                    <>
                      <button data-testid={`schedule-quiz-${q.id}`} onClick={() => openSched(q)} className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition" title="Jadwal buka/tutup">
                        <CalendarClock className="h-4 w-4" />
                      </button>
                      <button data-testid={`manage-quiz-${q.id}`} onClick={() => navigate(`/kuis/${q.id}/edit`)} className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
                        <Pencil className="h-4 w-4" /> Kelola
                      </button>
                      <button data-testid={`delete-quiz-${q.id}`} onClick={() => removeQuiz(q.id)} className="flex items-center justify-center rounded-xl border border-slate-200 px-3 py-2.5 text-slate-400 hover:text-rose-600 hover:border-rose-200 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <button data-testid={`view-questions-${q.id}`} onClick={() => openView(q)} className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
                      <Eye className="h-4 w-4" /> Lihat Soal
                    </button>
                  )}
                </div>
              ) : best !== null ? (
                <div data-testid={`quiz-done-${q.id}`} className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="h-4 w-4" /> Sudah Dikerjakan · Nilai {best}
                </div>
              ) : avail.is_available ? (
                <button data-testid={`take-quiz-${q.id}`} onClick={() => navigate(`/kuis/${q.id}`)} className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
                  <Play className="h-4 w-4" /> Mulai Kuis
                </button>
              ) : (
                <div data-testid={`quiz-locked-${q.id}`} className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-500 border border-slate-200">
                  <Lock className="h-4 w-4" /> {avail.status === "not_open" ? "Belum Dibuka" : "Sudah Ditutup"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {schedQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !savingSched && setSchedQuiz(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="schedule-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-xl font-bold text-slate-900">Jadwal & Buka/Tutup Kuis</h3>
              {!savingSched && <button onClick={() => setSchedQuiz(null)}><X className="h-5 w-5 text-slate-400" /></button>}
            </div>
            <p className="mb-5 text-sm text-slate-500 truncate">{schedQuiz.title}</p>
            <form onSubmit={saveSched} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mode</label>
                <div className="grid grid-cols-3 gap-2">
                  {[["auto", "Otomatis (ikuti jadwal)"], ["open", "Paksa Buka"], ["closed", "Paksa Tutup"]].map(([val, lbl]) => (
                    <button key={val} type="button" data-testid={`sched-mode-${val}`} onClick={() => setSched((s) => ({ ...s, manual_state: val }))}
                      className={`rounded-xl border px-2 py-2 text-xs font-semibold transition ${sched.manual_state === val ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div className={sched.manual_state !== "auto" ? "opacity-50 pointer-events-none" : ""}>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Waktu Buka (WIB)</label>
                <input data-testid="sched-open-input" type="datetime-local" value={sched.open_at} onChange={(e) => setSched((s) => ({ ...s, open_at: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                <label className="block text-sm font-semibold text-slate-700 mb-1.5 mt-3">Waktu Tutup (WIB)</label>
                <input data-testid="sched-close-input" type="datetime-local" value={sched.close_at} onChange={(e) => setSched((s) => ({ ...s, close_at: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                <p className="mt-2 text-xs text-slate-400">Kosongkan salah satu untuk tanpa batas. Mode "Paksa Buka/Tutup" mengabaikan jadwal ini.</p>
              </div>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Durasi pengerjaan (timer) diatur di menu "Kelola". Batas 1x pengerjaan per peserta tetap berlaku.</p>
              <button data-testid="save-schedule-button" type="submit" disabled={savingSched} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {savingSched ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Simpan Jadwal
              </button>
            </form>
          </div>
        </div>
      )}

      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !creating && setManualOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold text-slate-900">Tulis Soal Manual</h3>
              {!creating && <button onClick={() => setManualOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>}
            </div>
            <form onSubmit={createManual} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Judul Kuis</label>
                <input data-testid="manual-title-input" required value={mTitle} onChange={(e) => setMTitle(e.target.value)} placeholder="mis. Kuis Etika Kerja" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Batas Waktu (menit)</label>
                <input data-testid="manual-duration-input" type="number" min="1" max="180" required value={mDuration} onChange={(e) => setMDuration(e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <p className="text-xs text-slate-500">Setelah dibuat, Anda akan diarahkan ke Bank Soal untuk menulis soal sendiri.</p>
              <button data-testid="create-manual-button" type="submit" disabled={creating} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} Buat & Tulis Soal
              </button>
            </form>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !generating && setOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold text-slate-900">Buat Kuis Otomatis</h3>
              {!generating && <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>}
            </div>
            <form onSubmit={generate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Judul Kuis</label>
                  <input data-testid="quiz-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="mis. Keselamatan Kerja" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Batas Waktu (menit)</label>
                  <input data-testid="quiz-duration-input" type="number" min="1" max="180" required value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">File Materi (PDF, DOCX, atau TXT)</label>
                <input ref={fileRef} data-testid="quiz-upload-input" type="file" accept=".pdf,.docx,.doc,.txt" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" />
                <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition">
                  <Upload className="h-5 w-5" /> {file ? file.name : "Pilih file materi"}
                </button>
              </div>
              {generating && (
                <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
                  <p className="text-sm text-orange-700">AI sedang membaca materi dan menyusun 10 soal...</p>
                </div>
              )}
              <button data-testid="quiz-generate-button" type="submit" disabled={generating} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Soal
              </button>
            </form>
          </div>
        </div>
      )}
      {detailAttempt && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDetailAttempt(null)} />
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="attempt-detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-slate-900">Detail Jawaban</h3>
                {!detailLoading && detailAttempt.employee_name && (
                  <p className="text-sm text-slate-500">{detailAttempt.employee_name} · {detailAttempt.employee_id} · Nilai {detailAttempt.score} · Benar {detailAttempt.correct_count}/{detailAttempt.total}</p>
                )}
              </div>
              <button onClick={() => setDetailAttempt(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            {detailLoading || detailAttempt.loading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : (
              <div className="space-y-3">
                {detailAttempt.auto_submitted && (
                  <p className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600"><ShieldAlert className="h-3.5 w-3.5" /> Dikumpulkan otomatis karena pelanggaran</p>
                )}
                {(detailAttempt.results || []).map((r, i) => {
                  const sel = r.selected || [];
                  const cor = r.correct_indices || [];
                  const credit = r.credit != null ? r.credit : 0;
                  const full = credit >= 1;
                  const none = credit <= 0;
                  return (
                    <div key={i} data-testid={`detail-question-${i}`} className="rounded-xl border border-slate-200 p-4">
                      <p className="font-semibold text-slate-800">{i + 1}. {r.question}{cor.length > 1 && <span className="ml-2 text-xs font-normal text-slate-400">(jawaban ganda)</span>}</p>
                      <div className="mt-3 space-y-2">
                        {r.options.map((opt, oi) => {
                          const isCorrect = cor.includes(oi);
                          const isSelected = sel.includes(oi);
                          return (
                            <div key={oi} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${
                              isCorrect ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                              isSelected ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-100 text-slate-600"
                            }`}>
                              {isCorrect ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : isSelected ? <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white">✕</span> : <span className="h-4 w-4 shrink-0" />}
                              {opt}
                              {isSelected && <span className="ml-auto text-[10px] font-bold uppercase text-slate-400">dipilih</span>}
                            </div>
                          );
                        })}
                      </div>
                      <p className={`mt-2 text-xs font-semibold ${full ? "text-emerald-600" : none ? "text-rose-600" : "text-amber-600"}`}>
                        {full ? "Jawaban benar" : none ? "Jawaban salah" : `Benar sebagian — poin ${Math.round(credit * 100)}%`}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {viewQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setViewQuiz(null)} />
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="quiz-view-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-xl font-bold text-slate-900">{viewQuiz.meta.title}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${viewQuiz.meta.published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {viewQuiz.meta.published ? "PUBLIK" : "DRAFT"}
                  </span>
                </div>
                <p className="text-sm text-slate-500">{viewQuiz.meta.question_count} soal · {viewQuiz.meta.duration_minutes} menit · tampilan baca-saja</p>
              </div>
              <button onClick={() => setViewQuiz(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            {viewLoading || viewQuiz.questions === null ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : viewQuiz.questions.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">Kuis ini belum memiliki soal.</p>
            ) : (
              <div className="space-y-4">
                {viewQuiz.questions.map((q, qi) => {
                  const correct = q.correct_indices || [];
                  return (
                    <div key={qi} data-testid={`view-question-${qi}`} className="rounded-xl border border-slate-200 p-4">
                      <p className="font-semibold text-slate-800">
                        <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-lg bg-orange-50 text-xs font-bold text-orange-600">{qi + 1}</span>
                        {q.question}
                        {correct.length > 1 && <span className="ml-2 text-xs font-normal text-slate-400">(jawaban ganda)</span>}
                      </p>
                      <div className="mt-3 space-y-2">
                        {q.options.map((opt, oi) => {
                          const isCorrect = correct.includes(oi);
                          return (
                            <div key={oi} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${isCorrect ? "border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-100 text-slate-600"}`}>
                              {isCorrect ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-400">{String.fromCharCode(65 + oi)}</span>}
                              {opt}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {resultsQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setResultsQuiz(null)} />
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="quiz-results-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-slate-900">Hasil & Pelanggaran</h3>
                <p className="text-sm text-slate-500">{resultsQuiz.title}</p>
              </div>
              <button onClick={() => setResultsQuiz(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            {(() => {
              const rows = attempts.filter((a) => a.quiz_id === resultsQuiz.id);
              if (rows.length === 0) return <p className="py-10 text-center text-sm text-slate-400">Belum ada peserta yang mengerjakan kuis ini.</p>;
              const labelMap = {
                pindah_tab: "Pindah tab", keluar_jendela: "Keluar jendela", keluar_fullscreen: "Keluar layar penuh",
                klik_kanan: "Klik kanan", salin_teks: "Salin teks", tempel_teks: "Tempel teks",
                print_screen: "Tangkapan layar", devtools_shortcut: "Pintasan DevTools", devtools_terbuka: "DevTools terbuka",
              };
              return (
                <div className="space-y-3">
                  {rows.map((a) => {
                    const counts = {};
                    (a.violation_events || []).forEach((e) => { counts[e.type] = (counts[e.type] || 0) + 1; });
                    return (
                      <div key={a.id} data-testid={`attempt-row-${a.employee_id}`} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">{a.employee_name}</p>
                            <p className="text-xs text-slate-400">{a.employee_id} · Benar {a.correct_count}/{a.total}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {a.auto_submitted && <span className="rounded-full bg-rose-100 px-2.5 py-1 text-[10px] font-bold text-rose-700">AUTO-SUBMIT</span>}
                            <span className="rounded-full bg-slate-900 px-3 py-1 text-sm font-bold text-white">{a.score}</span>
                          </div>
                        </div>
                        {(a.violation_count > 0 || (a.violation_events || []).length > 0) ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600"><ShieldAlert className="h-3.5 w-3.5" /> {a.violation_count || 0} pelanggaran utama</span>
                            {Object.entries(counts).map(([t, c]) => (
                              <span key={t} className="rounded-full bg-orange-50 px-2.5 py-0.5 text-[11px] font-medium text-orange-700">{labelMap[t] || t} ×{c}</span>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-xs font-medium text-emerald-600">Tidak ada pelanggaran terdeteksi</p>
                        )}
                        <button data-testid={`view-answers-${a.employee_id}`} onClick={() => openDetail(a.id)} className="mt-3 flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition">
                          <Eye className="h-3.5 w-3.5" /> Lihat Jawaban
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
}
